/**
 * Step navigation utilities for the onboarding flow.
 * Provides key-based navigation instead of index-based navigation.
 */

export type StepKey =
  | 'ROLE_SELECTION'
  | 'COMPLETE_ACCOUNT'
  | 'ACTIVATION_CODE'
  | 'SELECT_PLAN'
  | 'INVITE_FAMILY';

/**
 * The canonical step order - single source of truth for step sequence.
 */
export const STEP_ORDER: readonly StepKey[] = [
  'ROLE_SELECTION',
  'COMPLETE_ACCOUNT',
  'ACTIVATION_CODE',
  'SELECT_PLAN',
  'INVITE_FAMILY',
] as const;

/**
 * Get the index of a step key in the step order.
 * Useful when an index is needed (e.g., progress calculations).
 */
export function getStepIndex(stepKey: StepKey): number {
  return STEP_ORDER.indexOf(stepKey);
}

/**
 * Get a step key from an index.
 * Returns undefined if index is out of bounds.
 */
export function getStepKey(index: number): StepKey | undefined {
  return STEP_ORDER[index];
}

/**
 * Get the next step key after the current step.
 * Returns undefined if at the last step.
 */
export function getNextStep(currentStep: StepKey): StepKey | undefined {
  const currentIndex = STEP_ORDER.indexOf(currentStep);

  return STEP_ORDER[currentIndex + 1];
}

/**
 * Get the previous step key before the current step.
 * Returns undefined if at the first step.
 */
export function getPreviousStep(currentStep: StepKey): StepKey | undefined {
  const currentIndex = STEP_ORDER.indexOf(currentStep);

  return currentIndex > 0 ? STEP_ORDER[currentIndex - 1] : undefined;
}

/**
 * Check if a step is at or past another step in the flow.
 */
export function isStepAtOrPast(current: StepKey, target: StepKey): boolean {
  return getStepIndex(current) >= getStepIndex(target);
}

/**
 * Check if a step is before another step in the flow.
 */
export function isStepBefore(current: StepKey, target: StepKey): boolean {
  return getStepIndex(current) < getStepIndex(target);
}

/**
 * Maps step keys to their corresponding sidebar item indices.
 * Used to determine which sidebar item should be highlighted for each step.
 *
 * Sidebar items:
 * - Index 0: "Create account" (signup page - not navigable during onboarding)
 * - Index 1: "Your role" (ROLE_SELECTION, COMPLETE_ACCOUNT)
 * - Index 2: "Select your plan" (ACTIVATION_CODE, SELECT_PLAN)
 * - Index 3: "Invite family" (INVITE_FAMILY)
 */
export const STEP_TO_SIDEBAR_INDEX: Record<StepKey, number> = {
  ROLE_SELECTION: 1,
  COMPLETE_ACCOUNT: 1,
  ACTIVATION_CODE: 2,
  SELECT_PLAN: 2,
  INVITE_FAMILY: 3,
};
